import { useContext, useState } from "react";
import { KeyParty } from "../../../../data/dtos/get-key-party.dto";
import { useDeleteKeyPartyMutation } from "../../../../data/useProfile";
import ErrorSnackbar from "../../../shared/ErrorSnackbar";
import { SnackbarContext } from "../../../shared/Snackbar";
import {
  useMediaQuery,
  WhmButton,
  WhmDialog,
  WhmDialogActions,
  WhmDialogContent,
  WhmDialogTitle,
  WhmIconButton,
  WhmLoadingButton,
  whmTheme,
  WhmTypography,
} from "@securitize/reactjs-whm";
import { Translate, TranslateHtml } from "../../../Translation/Translate";
import { I18nContext, translationKeys } from "../../../../contexts/I18nContext";
import { useQueryClient } from "@tanstack/react-query";
import { ReactComponent as CloseIcon } from "@securitize/assets-sec-ui/dist/icons/svg/exit.svg";

export default function DeleteKeyPartyDialog({
  open,
  keyParty,
  onClose,
}: {
  open: boolean;
  keyParty?: KeyParty;
  onClose: () => void;
}) {
  const deleteKeyPartyMutation = useDeleteKeyPartyMutation();
  const snackbarContext = useContext(SnackbarContext);
  const { getTranslation } = useContext(I18nContext);
  const queryClient = useQueryClient();
  const isMobile = useMediaQuery(whmTheme.breakpoints.down("xl"));
  const [isWorking, setIsWorking] = useState(false);

  const handleDelete = () => {
    if (!keyParty) return;
    setIsWorking(true);
    deleteKeyPartyMutation.mutate(
      { keyPartyId: keyParty.id },
      {
        onSuccess: () => {
          queryClient
            .invalidateQueries({
              predicate: (query) =>
                ["key-parties", "onboarding", "pending-actions"].includes(
                  query.queryKey[0] as string,
                ),
            })
            .then(() => {
              snackbarContext.showSnackbar({
                message: getTranslation(
                  translationKeys.PROFILE_KEY_PARTIES_DELETE_SUCCESS_MESSAGE,
                  {
                    name: keyParty.displayName,
                  },
                ),
                severity: "info",
              });

              setIsWorking(false);
              onClose();
            })
            .catch(() => {
              setIsWorking(false);
              snackbarContext.showSnackbar({
                message: <ErrorSnackbar />,
                severity: "error",
              });
            });
        },
        onError: (error) => {
          snackbarContext.showSnackbar({
            message: <ErrorSnackbar error={error} />,
            severity: "error",
          });
          setIsWorking(false);
        },
      },
    );
  };

  return (
    <WhmDialog
      open={open}
      onClose={onClose}
      maxWidth={isMobile ? "xl" : "xs"}
      sx={{
        "& .MuiDialog-paper": {
          margin: isMobile ? "24px" : undefined,
        },
      }}
    >
      <WhmDialogTitle
        sx={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          marginRight: "32px",
        }}
        variant="h4"
      >
        <Translate
          translationKey={
            translationKeys.PROFILE_KEY_PARTIES_DELETE_CONFIRMATION_TITLE
          }
          replacements={{
            name: keyParty?.displayName ?? "",
          }}
        />
      </WhmDialogTitle>
      <WhmIconButton
        onClick={onClose}
        sx={{
          position: "absolute",
          right: "8px",
          top: "8px",
        }}
      >
        <CloseIcon />
      </WhmIconButton>
      <WhmDialogContent>
        <WhmTypography>
          <TranslateHtml
            translationKey={
              translationKeys.PROFILE_KEY_PARTIES_DELETE_CONFIRMATION_SUBTITLE
            }
          />
        </WhmTypography>
      </WhmDialogContent>
      <WhmDialogActions>
        <WhmButton onClick={onClose} color="secondary" variant="outlined">
          <Translate
            translationKey={
              translationKeys.PROFILE_KEY_PARTIES_DELETE_CONFIRMATION_CANCEL
            }
          />
        </WhmButton>
        <WhmLoadingButton
          onClick={handleDelete}
          color="error"
          loading={isWorking}
          variant="contained"
          sx={{
            svg: {
              display: "block !important",
            },
            "&.MuiLoadingButton-loading": {
              color: "black",
              opacity: "0.3",
              span: {
                color: "black",
              },
            },
          }}
        >
          {isWorking ? (
            <Translate
              translationKey={
                translationKeys.PROFILE_KEY_PARTIES_DELETE_CONFIRMATION_DELETING
              }
            />
          ) : (
            <Translate
              translationKey={
                translationKeys.PROFILE_KEY_PARTIES_DELETE_CONFIRMATION_DELETE
              }
            />
          )}
        </WhmLoadingButton>
      </WhmDialogActions>
    </WhmDialog>
  );
}
